import { createContext, useContext, useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { setCurrentContact, fetchContact, archiveContact, restoreContact } from './contactsSlice'

import Modal from '@/components/ui/Modal'
import { capitalize } from '@/utils/strings'
import { formatDate } from '@/utils/formatters'

import GeneralInformation from './show/GeneralInformation'
import BiensList from './show/BiensList'
import GLIInformation from './show/GLIInformation'
import ContactRoles from './ContactRoles'
import EditButtons from './EditButtons'
import ContactForm from './form/ContactForm'
import DocumentsList from './show/DocumentsList'

const ContactContext = createContext()

export const useContactContext = () => useContext(ContactContext)

// Composant pour le titre
const ContactTitle = () => {
  const { contact } = useContactContext()

  return (
    <div className={`flex items-start ${contact.archived_at && 'tooltip'}`}>
      {contact.archived_at && (
        <div className='tooltip-content'>
          archivé le {formatDate(contact.archived_at)}
        </div>
      )}
      <h1 className="text-3xl font-bold flex items-center gap-3">
        {capitalize(contact.prenom)} {contact.nom && contact.nom.toUpperCase()} 
      </h1>
      <div className="badge-container ml-2">
        {contact.archived_at && (
          <span className="badge badge-ghost px-3 py-3 text-xs">
            archivé
          </span>
        )}
      </div>
    </div>
  )
}

// Composant pour le type de contact
const ContactType = () => {
  const { contact } = useContactContext()
  return (
    <div className="flex flex-col justify-end">
      <p className="block text-sm font-medium mb-1">Type de contact</p>
      <ContactRoles roles={contact.roles} /> 
    </div>
  )
}

const ContactShow = ({ contact }) => {
  const dispatch = useDispatch()
  const currentContact = useSelector((state) => state.contacts.current)
  const loading = useSelector((state) => state.contacts.loadingStatus.fetchItem)
  const updateLoading = useSelector((state) => state.contacts.loadingStatus.update)
  const archiveLoading = useSelector((state) => state.contacts.loadingStatus.archive)
  const restoreLoading = useSelector((state) => state.contacts.loadingStatus.restore)
  const [isPendingAction, setIsPendingAction] = useState(false) // Contact en attente de réponse API
  const [wasUpdated, setWasUpdated] = useState(false) // État pour suivre les mises à jour

  const { isOpen, handleShow, handleHide, dialogRef } = Modal.useModal()

  // Exécuté uniquement au premier montage du composant
  useEffect(() => {
    dispatch(setCurrentContact(contact))
    dispatch(fetchContact(contact.id)) 
  }, [contact.id]) // Tableau de dépendances vide

  useEffect(() => {
    if (!archiveLoading && !restoreLoading && isPendingAction){
      setIsPendingAction(false)
    }
    if(updateLoading || archiveLoading || restoreLoading){
      setIsPendingAction(true)
    }
    if(loading && !isPendingAction) {
      setIsPendingAction(true)
    }
    if(!loading && isPendingAction) {
      setIsPendingAction(false)
    }
  }, [loading, updateLoading, archiveLoading, restoreLoading])

  // Nouvel effet pour détecter les mises à jour
  useEffect(() => {
    if (!updateLoading && isPendingAction) {
      setWasUpdated(true)
      // Réinitialiser l'effet après un court délai
      const timer = setTimeout(() => {
        setWasUpdated(false)
      }, 2000)
      
      return () => clearTimeout(timer)
    }
  }, [updateLoading, isPendingAction])
 
  const handleEditClick = () => {
    handleShow()
  }

  const handleArchiveClick = () => {
    setIsPendingAction(true)
    dispatch( !!currentContact.archived_at ? 
      restoreContact(currentContact.id) :
      archiveContact(currentContact.id)
    )
  }

  return (
    <>
    <ContactContext.Provider value={{
      contact: currentContact || contact, 
      wasUpdated,
      loading, isPendingAction,
      setIsPendingAction
    }}>
      <div className="show-container">
        <div className="title-container flex justify-between mb-4">
          <ContactTitle />
          <div className="flex items-center justify-end space-x-4">
            <ContactType />
            <div>
              <EditButtons 
                contactId={currentContact?.id || contact.id}
                isArchived={!!currentContact?.archived_at}
                isPendingAction={isPendingAction}
                handleEditClick={handleEditClick}
                handleArchiveClick={handleArchiveClick}
              />
            </div>
          </div>
        </div>
        <div className='content-container px-4'>
          <GeneralInformation />
          <BiensList />
          { currentContact?.type === "PersonnePhysique" && <GLIInformation /> }
          
          <div className="grid grid-cols-2 space-x-4">
            {/* Section Décomptes (s'affiche uniquement s'il y a des décomptes) */}
            <DocumentsList type="decompte" title="Décomptes" />

            {/* Section Documents Légaux pour tous les contacts */}
            <DocumentsList type="document_legal" title="Documents Légaux" />
          </div>
        </div>
      </div>
    </ContactContext.Provider>
    <Modal
      open={isOpen}
      onClose={handleHide}
      ref={dialogRef}
      backdrop={false}
      className="max-w-full rounded-none"
    >
      <Modal.Header showCloseButton={false} />
      <Modal.Body>
        <ContactForm fixType={true} onSuccess={handleHide} onClose={handleHide} />
      </Modal.Body>
    </Modal>
    </>
  )
}

export default ContactShow